/**
 * Edad y sexo al marcar «Citado» en Leads (dirección, 16/10/2026).
 *
 * Para saber a qué edades y a qué sexo corresponden las citas que traen los
 * anuncios, al citar a alguien desde Leads se pregunta:
 *  · la FECHA DE NACIMIENTO, que solo se deja en blanco marcando «No la
 *    sabe»: así es una decisión y no un olvido;
 *  · el SEXO, obligatorio de elegir, aunque sea «No consta»;
 *  · y DE QUIÉN ES LA FICHA (revisión del 16/10/2026): un teléfono o un
 *    correo puede ser de una familia entera (madre e hija), así que nunca se
 *    da por hecho. Se elige «Es la ficha de …» entre las que tienen su
 *    teléfono o su correo, o «Ninguna: guardar solo en el contacto»; con una
 *    sola, se pregunta igual.
 *
 * Empieza relleno con lo que se dijo la vez anterior. Al elegir una ficha,
 * lo que aún esté vacío se rellena con lo que ella diga. El servidor
 * (PUT /api/leads/{id}/citado) guarda siempre en el contacto y, en la ficha
 * elegida, SOLO rellena huecos: lo que la ficha ya diga distinto no se pisa,
 * y lo avisa.
 *
 * Con `completar` (un contacto ya marcado «Citado» a mano): el mismo diálogo,
 * para completar su edad y su sexo.
 */
import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { OPCIONES_SEXO, claveSexo } from '../lib/sexo';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { CalendarCheck, Loader2 } from 'lucide-react';

export const NINGUNA = 'ninguna';

/** «AAAA-MM-DD» de hoy, en el reloj de quien mira (el tope del calendario). */
function hoyIso() {
  const d = new Date();
  const dos = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${dos(d.getMonth() + 1)}-${dos(d.getDate())}`;
}

/** «1984-05-06» → «06/05/1984». */
function fechaLegible(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso || '');
  return m ? `${m[3]}/${m[2]}/${m[1]}` : '';
}

/** Lo que falta para poder citar, en cristiano (vacío si nada). */
export function queFalta({ fecha, noLaSabe, sexo, ficha, hayCandidatas }) {
  return [
    hayCandidatas && !ficha ? 'elegir de quién es la ficha (o «Ninguna»)' : '',
    !OPCIONES_SEXO.includes(sexo) ? 'el sexo (también vale «No consta»)' : '',
    !noLaSabe && !/^\d{4}-\d{2}-\d{2}$/.test(fecha || '') ? 'la fecha de nacimiento o «No la sabe»' : '',
  ].filter(Boolean);
}

/** Lo que se manda al servidor con «Citado», o null si aún falta algo. */
export function datosParaCitar({ fecha, noLaSabe, sexo, ficha, hayCandidatas = false }) {
  if (queFalta({ fecha, noLaSabe, sexo, ficha, hayCandidatas }).length) return null;
  const pacienteId = ficha && ficha !== NINGUNA ? ficha : null;
  if (noLaSabe) return { sexo, fecha_no_la_sabe: true, paciente_id: pacienteId };
  return { sexo, fecha_nacimiento: fecha, paciente_id: pacienteId };
}

export default function DialogoCitarEdadSexo({ lead, onCerrar, onConfirmar, guardando, completar = false }) {
  const [cargando, setCargando] = useState(true);
  const [fallo, setFallo] = useState(false);
  const [candidatas, setCandidatas] = useState([]);
  const [ficha, setFicha] = useState('');
  const [fecha, setFecha] = useState('');
  const [noLaSabe, setNoLaSabe] = useState(false);
  const [sexo, setSexo] = useState('');

  useEffect(() => {
    if (!lead?.id) return undefined;
    let vivo = true;
    setCargando(true);
    setFallo(false);
    setCandidatas([]);
    setFicha('');
    setFecha('');
    setNoLaSabe(false);
    setSexo('');
    api.get(`/api/leads/${lead.id}/datos-al-citar`)
      .then((r) => {
        if (!vivo) return;
        const d = r?.data || {};
        const guardado = d.guardado || null;
        setCandidatas(Array.isArray(d.candidatas) ? d.candidatas : []);
        setFecha(guardado?.fecha_nacimiento || '');
        setNoLaSabe(!guardado?.fecha_nacimiento && guardado?.fecha_no_la_sabe === true);
        setSexo(OPCIONES_SEXO.includes(guardado?.sexo) ? guardado.sexo : '');
      })
      // Sin mirar la ficha se pregunta igual, y se guarda solo en el contacto.
      .catch(() => { if (vivo) setFallo(true); })
      .finally(() => { if (vivo) setCargando(false); });
    return () => { vivo = false; };
  }, [lead?.id]);

  if (!lead) return null;
  const hayCandidatas = candidatas.length > 0;
  const estado = { fecha, noLaSabe, sexo, ficha, hayCandidatas };
  const faltan = queFalta(estado);
  const datos = datosParaCitar(estado);

  const elegirFicha = (id) => {
    setFicha(id);
    const c = candidatas.find((x) => x.id === id);
    if (!c) return;
    // Lo que aún está vacío, con lo que dice esa ficha (se puede cambiar).
    if (!fecha && !noLaSabe && c.fecha_nacimiento) setFecha(c.fecha_nacimiento);
    if (!sexo && OPCIONES_SEXO.includes(c.sexo)) setSexo(c.sexo);
  };

  const donde = fallo
    ? 'No se ha podido mirar la ficha: se guarda solo en el contacto.'
    : hayCandidatas
      ? 'En la ficha elegida solo se rellena lo que tenga vacío: lo que ya diga otra cosa no se cambia (se avisa).'
      : 'Todavía no tiene ficha: se guarda en el contacto y pasará a su ficha cuando se dé de alta con su teléfono o su correo y su nombre.';

  return (
    <Dialog open onOpenChange={(abierta) => { if (!abierta) onCerrar(); }}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="lead-citar-dialogo">
        <DialogHeader>
          <DialogTitle>
            {completar ? 'Edad y sexo' : 'Citado'}{lead.nombre ? `: ${lead.nombre}` : ''}
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">
          {completar
            ? 'Su fecha de nacimiento y su sexo: con ellos se sabe a quién le llegan de verdad las campañas.'
            : 'Antes de pasarlo a «Ya citados», su fecha de nacimiento y su sexo: con ellos se sabe a quién le llegan de verdad las campañas.'}
        </p>
        {cargando ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin" /> Mirando su ficha…
          </div>
        ) : (
          <div className="space-y-4">
            {hayCandidatas ? (
              <fieldset className="space-y-1.5" data-testid="lead-citar-fichas">
                <legend className="text-sm font-medium">¿De quién es la ficha?</legend>
                <p className="text-xs text-muted-foreground">
                  {candidatas.length === 1 ? 'Hay una ficha' : `Hay ${candidatas.length} fichas`} con su
                  teléfono o su correo. Un teléfono puede ser de toda la familia: confírmalo.
                </p>
                {candidatas.map((c) => (
                  <label key={c.id} className="flex items-start gap-2 rounded border p-2 text-sm">
                    <input type="radio" name="lead-citar-ficha" className="mt-1" checked={ficha === c.id}
                      data-testid={`lead-citar-ficha-${c.id}`} onChange={() => elegirFicha(c.id)} />
                    <span>
                      <span className="font-medium">Es la ficha de {c.nombre || 'sin nombre'}</span>
                      <span className="block text-xs text-muted-foreground">
                        {[c.fecha_nacimiento ? `nacida/o el ${fechaLegible(c.fecha_nacimiento)}` : 'sin fecha de nacimiento',
                          c.sexo || (c.sexo_antiguo ? `sexo «${c.sexo_antiguo}» (dato antiguo)` : 'sin sexo'),
                          c.tiene_su_cita ? 'tiene la cita de este contacto' : ''].filter(Boolean).join(' · ')}
                      </span>
                      {c.fecha_nota ? (
                        <span className="block text-xs text-amber-800" data-testid={`lead-citar-fecha-nota-${c.id}`}>
                          {c.fecha_nota}
                        </span>
                      ) : null}
                    </span>
                  </label>
                ))}
                <label className="flex items-start gap-2 rounded border p-2 text-sm">
                  <input type="radio" name="lead-citar-ficha" className="mt-1" checked={ficha === NINGUNA}
                    data-testid="lead-citar-ficha-ninguna" onChange={() => setFicha(NINGUNA)} />
                  <span>Ninguna: guardar solo en el contacto</span>
                </label>
              </fieldset>
            ) : null}
            <div className="space-y-1.5">
              <Label htmlFor="lead-citar-fecha">Fecha de nacimiento</Label>
              <Input id="lead-citar-fecha" type="date" value={noLaSabe ? '' : fecha} max={hoyIso()}
                disabled={noLaSabe} data-testid="lead-citar-fecha"
                onChange={(e) => setFecha(e.target.value)} />
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={noLaSabe} data-testid="lead-citar-no-la-sabe"
                  onChange={(e) => { setNoLaSabe(e.target.checked); if (e.target.checked) setFecha(''); }} />
                No la sabe (o no la quiere dar)
              </label>
            </div>
            <fieldset className="space-y-1.5">
              <legend className="text-sm font-medium">Sexo</legend>
              <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label="Sexo">
                {OPCIONES_SEXO.map((o) => (
                  <button key={o} type="button" role="radio" aria-checked={sexo === o}
                    data-testid={`lead-citar-sexo-${claveSexo(o)}`} onClick={() => setSexo(o)}
                    className={`rounded-full border px-3 py-1 text-sm transition-colors ${
                      sexo === o ? 'bg-[#2E6E5E] text-white border-[#2E6E5E]'
                        : 'bg-background text-muted-foreground hover:text-foreground'}`}>
                    {o}
                  </button>
                ))}
              </div>
            </fieldset>
            <p className={`text-xs ${fallo ? 'text-amber-800' : 'text-muted-foreground'}`}
              data-testid="lead-citar-donde">{donde}</p>
            {faltan.length ? (
              <p className="text-xs text-amber-800" data-testid="lead-citar-falta">
                Falta {faltan.join(', ')}.
              </p>
            ) : null}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onCerrar} data-testid="lead-citar-cancelar">Cancelar</Button>
          {/* Si falta algo, el botón lo dice en vez de quedarse apagado sin más. */}
          <Button onClick={() => datos && onConfirmar(datos)} disabled={cargando || guardando || !datos}
            className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="lead-citar-confirmar">
            {guardando ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CalendarCheck className="w-4 h-4 mr-2" />}
            {cargando ? 'Mirando la ficha…' : faltan.length ? `Falta ${faltan[0]}` : (completar ? 'Guardar' : 'Citado')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
